import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, RotateCcw, BookOpen } from 'lucide-react';
import api from '../services/api';
import AdminNavigation from '../components/AdminNavigation';

const MYSTERIES = {
  gozosos: {
    title: 'Mistérios Gozosos',
    subtitle: 'Segundas e Sábados',
    decades: [
      'A Anunciação do Anjo a Maria',
      'A Visitação de Maria a Isabel',
      'O Nascimento de Jesus em Belém',
      'A Apresentação do Menino Jesus no Templo',
      'A Perda e o Encontro de Jesus no Templo'
    ]
  },
  dolorosos: {
    title: 'Mistérios Dolorosos',
    subtitle: 'Terças e Sextas',
    decades: [
      'A Agonia de Jesus no Horto das Oliveiras',
      'A Flagelação de Nosso Senhor',
      'A Coroação de Espinhos',
      'Jesus Carrega a Cruz até o Monte Calvário',
      'A Crucificação e Morte de Jesus'
    ]
  },
  gloriosos: {
    title: 'Mistérios Gloriosos',
    subtitle: 'Quartas e Domingos',
    decades: [
      'A Ressurreição de Jesus',
      'A Ascensão de Jesus aos Céus',
      'A Vinda do Espírito Santo',
      'A Assunção de Maria aos Céus',
      'A Coroação de Maria como Rainha do Céu e da Terra'
    ]
  },
  luminosos: {
    title: 'Mistérios Luminosos',
    subtitle: 'Quintas-feiras',
    decades: [
      'O Batismo de Jesus no Rio Jordão',
      'A Auto-revelação nas Bodas de Caná',
      'O Anúncio do Reino de Deus',
      'A Transfiguração de Jesus',
      'A Instituição da Eucaristia'
    ]
  }
};

function defaultMysteryForDay(date = new Date()) {
  const day = date.getDay();
  switch (day) {
    case 0: return 'gloriosos';
    case 1: return 'gozosos';
    case 2: return 'dolorosos';
    case 3: return 'gloriosos';
    case 4: return 'luminosos';
    case 5: return 'dolorosos';
    case 6: return 'gozosos';
    default: return 'gozosos';
  }
}

function buildSteps(mysteryKey) {
  const mystery = MYSTERIES[mysteryKey] || MYSTERIES.gozosos;
  const steps = [];

  // 1. Sinal da Cruz (Crucifixo)
  steps.push({ 
    id: 'cross', 
    label: 'Sinal da Cruz e Credo', 
    prayer: 'Creio em Deus Pai Todo-Poderoso, Criador do céu e da terra...',
    beadId: 'cross'
  });

  // 2. Pai Nosso (Primeira conta grande - logo após a cruz)
  steps.push({ 
    id: 'intro-of', 
    label: 'Pai Nosso', 
    subLabel: 'Pelas intenções do Santo Padre',
    prayer: 'Pai Nosso que estais nos céus, santificado seja o vosso nome...',
    beadId: 'intro-pendant-large'
  });

  // 3. Três Ave-Marias (Contas pequenas)
  steps.push({ id: 'intro-ave-1', label: 'Ave Maria', subLabel: 'Pelo aumento da Fé', prayer: 'Ave Maria, cheia de graça...', beadId: 'intro-pendant-small-1' });
  steps.push({ id: 'intro-ave-2', label: 'Ave Maria', subLabel: 'Pelo aumento da Esperança', prayer: 'Ave Maria, cheia de graça...', beadId: 'intro-pendant-small-2' });
  steps.push({ id: 'intro-ave-3', label: 'Ave Maria', subLabel: 'Pelo aumento da Caridade', prayer: 'Ave Maria, cheia de graça...', beadId: 'intro-pendant-small-3' });

  // 4. Glória (Medalha)
  steps.push({ 
    id: 'intro-glory', 
    label: 'Glória ao Pai', 
    prayer: 'Glória ao Pai, ao Filho e ao Espírito Santo. Como era no princípio, agora e sempre. Amém.',
    beadId: 'medal' 
  });

  // Mistérios
  mystery.decades.forEach((decadeTitle, i) => {
    const decadeNum = i + 1;
    
    // Pai Nosso logic
    // 1st Mystery: Pai Nosso is on the Medal
    // Others: Pai Nosso is on the Large Bead preceding the decade
    const ofBeadId = i === 0 ? 'medal' : `loop-${i}-large`;

    steps.push({
      id: `d${decadeNum}-of`,
      label: `Pai Nosso`,
      subLabel: `${decadeNum}º Mistério: ${decadeTitle}`,
      prayer: 'Pai Nosso que estais nos céus...',
      beadId: ofBeadId,
      isMysteryStart: true,
      mysteryIndex: i
    });

    // 10 Aves
    for (let j = 1; j <= 10; j++) {
      steps.push({
        id: `d${decadeNum}-ave-${j}`,
        label: `Ave Maria ${j}`,
        subLabel: `${decadeNum}º Mistério: ${decadeTitle}`,
        prayer: 'Ave Maria, cheia de graça...',
        beadId: `loop-${i}-small-${j}`,
        mysteryIndex: i
      });
    }

    // Glória
    // Mapped to next large bead or medal
    // D1 Glory -> Loop 1 Large
    // D2 Glory -> Loop 2 Large
    // ...
    // D5 Glory -> Medal
    const nextBeadId = i === 4 ? 'medal' : `loop-${i+1}-large`;
    
    steps.push({
      id: `d${decadeNum}-glory`,
      label: 'Glória ao Pai',
      subLabel: `${decadeNum}º Mistério: ${decadeTitle}`,
      prayer: 'Glória ao Pai... Ó meu Jesus, perdoai-nos...', // Jaculatória included
      beadId: nextBeadId, 
      mysteryIndex: i,
      isGlory: true
    });
  });

  // Salve Rainha
  steps.push({
    id: 'salve-rainha',
    label: 'Salve Rainha',
    prayer: 'Salve Rainha, Mãe de Misericórdia, vida, doçura e esperança nossa, salve...',
    beadId: 'medal'
  });

  return steps;
}

// Fixed visual coordinates generation
const generateVisualMap = () => {
  const nodes = [];
  const cx = 200; // Center X
  const cy = 200; // Center Y
  const rx = 140; // Radius X
  const ry = 150; // Radius Y (slightly oval)
  
  // Calculate Loop Nodes
  // Decade 0: No Large Bead (Starts at Medal). 10 Small.
  // Decade 1, 2, 3, 4: 1 Large + 10 Small.
  
  // Total Beads on Loop = 10 (D0) + 11 (D1) + 11 (D2) + 11 (D3) + 11 (D4) = 54 beads?
  // + Medal acts as start/end.
  
  const totalLoopBeads = 54; 
  
  // Start angle logic
  // We want the loop to look balanced.
  // Gap at bottom for Medal (PI/2).
  // D0 starts LEFT of medal (Clockwise).
  // D4 ends RIGHT of medal.
  
  const startAngle = Math.PI / 2 + 0.35; 
  const endAngle = Math.PI / 2 + 2 * Math.PI - 0.35;
  const totalAngleRange = endAngle - startAngle;
  // We have 54 intervals/beads?
  // We need to place them carefully.
  const stepAngle = totalAngleRange / (totalLoopBeads - 1);
  
  let currentBeadGlobalIndex = 0;

  for (let d = 0; d < 5; d++) {
    // Determine beads for this decade
    const hasLarge = d !== 0; // Decade 0 has NO large bead in loop (uses medal)
    
    // If has large, place it
    if (hasLarge) {
        const largeIdx = currentBeadGlobalIndex;
        const currentAngle = startAngle + (largeIdx * stepAngle);
        nodes.push({ 
            id: `loop-${d}-large`, 
            x: cx + rx * Math.cos(currentAngle), 
            y: cy + ry * Math.sin(currentAngle), 
            type: 'large' 
        });
        currentBeadGlobalIndex++;
    }

    // Place 10 Small Beads
    for (let s = 1; s <= 10; s++) {
       const smallIdx = currentBeadGlobalIndex;
       const currentAngle = startAngle + (smallIdx * stepAngle);
       nodes.push({ 
           id: `loop-${d}-small-${s}`, 
           x: cx + rx * Math.cos(currentAngle), 
           y: cy + ry * Math.sin(currentAngle), 
           type: 'small' 
       });
       currentBeadGlobalIndex++;
    }
  }

  // Pendant
  // Medal is at bottom of loop (approx).
  const medalX = cx;
  const medalY = cy + ry + 15; // ~365
  nodes.push({ id: 'medal', x: medalX, y: medalY, type: 'medal' });

  // Pendant beads: Medal -> 3 Small -> 1 Large -> Cross
  const pendantStartY = medalY + 25; // ~390
  const pendantGap = 22;
  
  // 3 Small Beads (Closest to Medal)
  nodes.push({ id: 'intro-pendant-small-3', x: cx, y: pendantStartY, type: 'small' }); // ~390
  nodes.push({ id: 'intro-pendant-small-2', x: cx, y: pendantStartY + pendantGap, type: 'small' }); // ~412
  nodes.push({ id: 'intro-pendant-small-1', x: cx, y: pendantStartY + pendantGap * 2, type: 'small' }); // ~434
  
  // Large Bead (Gap, then Large, near Cross)
  const largeBeadY = pendantStartY + pendantGap * 3 + 10; // ~466
  nodes.push({ id: 'intro-pendant-large', x: cx, y: largeBeadY, type: 'large' });

  // Cross (Gap, then Cross)
  const crossY = largeBeadY + 35; // ~501
  nodes.push({ id: 'cross', x: cx, y: crossY, type: 'cross' });

  return nodes;
};

const VisualRosary = ({ currentNodeId, activeMysteryIndex, isMystery }) => {
  const nodes = useMemo(() => generateVisualMap(), []);

  return (
    <div className="relative flex justify-center py-4 select-none">
      <svg width="400" height="580" viewBox="0 0 400 580" className="drop-shadow-xl scale-90 md:scale-100">
        <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
            </feMerge>
            </filter>
            <linearGradient id="beadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f3f4f6" />
            <stop offset="100%" stopColor="#d1d5db" />
            </linearGradient>
            <linearGradient id="largeBeadGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#e0f2fe" />
            <stop offset="100%" stopColor="#7dd3fc" />
            </linearGradient>
            <linearGradient id="activeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fcd34d" />
            <stop offset="100%" stopColor="#f59e0b" />
            </linearGradient>
        </defs>

        {/* Wire/Chain - Simplified path connecting nodes roughly */}
        {/* Loop Ellipse */}
        <ellipse cx="200" cy="200" rx="140" ry="150" fill="none" stroke="#9ca3af" strokeWidth="2" opacity="0.5" />
        {/* Pendant Line: From Medal (365) to Top of Cross (~485) */}
        <line x1="200" y1="365" x2="200" y2="486" stroke="#9ca3af" strokeWidth="2" opacity="0.5" />

        {nodes.map((node) => {
          const isActive = currentNodeId === node.id;
          
          let r = 5;
          let fill = "url(#beadGradient)";
          
          if (node.type === 'large') {
            r = 8;
            fill = "url(#largeBeadGradient)";
          } else if (node.type === 'medal') {
             // Draw Medal shape
             return (
               <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
                 <path d="M -8 -10 L 8 -10 L 6 10 L -6 10 Z" fill={isActive ? "url(#activeGradient)" : "#94a3b8"} stroke="#475569" strokeWidth="1" filter={isActive ? "url(#glow)" : ""} />
               </g>
             );
          } else if (node.type === 'cross') {
             // Draw Cross
             return (
                <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
                    <rect x="-3" y="-15" width="6" height="30" fill={isActive ? "#fbbf24" : "#cbd5e1"} stroke="#64748b" strokeWidth="1" filter={isActive ? "url(#glow)" : ""} rx="1" />
                    <rect x="-12" y="-5" width="24" height="6" fill={isActive ? "#fbbf24" : "#cbd5e1"} stroke="#64748b" strokeWidth="1" filter={isActive ? "url(#glow)" : ""} rx="1" />
                </g>
             );
          }

          if (isActive) {
             fill = "url(#activeGradient)";
             r += 2; 
          }

          return (
            <circle 
              key={node.id} 
              cx={node.x} 
              cy={node.y} 
              r={r} 
              fill={fill} 
              stroke={isActive ? "#b45309" : "#94a3b8"} 
              strokeWidth={isActive ? 2 : 1}
              filter={isActive ? 'url(#glow)' : undefined}
              className="transition-all duration-300"
            />
          );
        })}
      </svg>
    </div>
  );
};

export default function TercoDiario() {
  const [mysteryKey, setMysteryKey] = useState(() => defaultMysteryForDay());
  const steps = useMemo(() => buildSteps(mysteryKey), [mysteryKey]);
  const [index, setIndex] = useState(0);
  const [remoteExists, setRemoteExists] = useState(false);
  const [savedIndexes, setSavedIndexes] = useState(() => {
    try {
      const raw = localStorage.getItem('rosary_saved_indexes');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });
  const mountedRef = useRef(true);

  const currentStep = steps[index];
  const progress = Math.round(((index + 1) / steps.length) * 100);
  
  
  const next = useCallback(() => {
    // Scroll prayer card to top when changing
    const prayerCard = document.getElementById('prayer-text');
    if (prayerCard) prayerCard.scrollTop = 0;
    setIndex((i) => Math.min(i + 1, steps.length - 1));
  }, [steps.length]);
  const prev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);
  const reset = useCallback(() => setIndex(0), []);

  const handleMysteryChange = useCallback((newKey) => {
    const restored = savedIndexes?.[newKey];
    setMysteryKey(newKey);
    setIndex(typeof restored === 'number' ? restored : 0);
  }, [savedIndexes]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        prev();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev]);

  // Load remote progress on mount
  useEffect(() => {
    mountedRef.current = true;
    (async () => {
      try {
        const resp = await api.get('/rosary/progress');
        const data = resp.data;
        if (!mountedRef.current || !data) return;
        if (data.mystery_key) setMysteryKey(data.mystery_key);
        if (typeof data.step_index === 'number') {
          const max = buildSteps(data.mystery_key || defaultMysteryForDay()).length - 1;
          const clamped = Math.max(0, Math.min(data.step_index, max));
          setIndex(clamped);
          setSavedIndexes(prev => {
            try {
              const next = { ...(prev || {}), [data.mystery_key]: clamped };
              localStorage.setItem('rosary_saved_indexes', JSON.stringify(next));
              return next;
            } catch (e) {
              return prev;
            }
          });
        }
        setRemoteExists(true);
      } catch (err) {
        // silent
      }
    })();
    return () => { mountedRef.current = false; };
  }, []);

  // Save progress (debounced) when index or mysteryKey changes
  useEffect(() => {
    const payload = {
      mysteryKey,
      stepIndex: index,
      totalSteps: steps.length,
      date: new Date().toISOString().slice(0, 10),
    };

    // eslint-disable-next-line no-console
    console.debug('TercoDiario: schedule save', payload);
    const t = setTimeout(async () => {
      try {
        if (remoteExists) {
          await api.put('/rosary/progress', payload);
          // persist local mapping
          setSavedIndexes(prev => {
            try {
              const next = { ...(prev || {}), [mysteryKey]: index };
              localStorage.setItem('rosary_saved_indexes', JSON.stringify(next));
              return next;
            } catch (e) { return prev; }
          });
        } else {
          await api.post('/rosary/progress', payload);
          setRemoteExists(true);
          setSavedIndexes(prev => {
            try {
              const next = { ...(prev || {}), [mysteryKey]: index };
              localStorage.setItem('rosary_saved_indexes', JSON.stringify(next));
              return next;
            } catch (e) { return prev; }
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('TercoDiario: error saving progress', err, { payload, remoteExists });
      }
    }, 700);

    return () => clearTimeout(t);
  }, [index, mysteryKey, remoteExists, steps.length]);

  // Current Mystery Info
  const currentMysteryInfo = useMemo(() => {
     if (currentStep.mysteryIndex !== undefined) {
         return MYSTERIES[mysteryKey].decades[currentStep.mysteryIndex];
     }
     return null;
  }, [currentStep, mysteryKey]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <AdminNavigation />
      
      <div className="flex-1 max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
        
        {/* Left Column: Visual Rosary */}
        <div className="bg-white rounded-3xl shadow-xl p-2 flex flex-col items-center justify-center relative ring-1 ring-slate-200 order-2 lg:order-1 min-h-[500px]">
             <div className="absolute top-4 left-4 z-10 w-48">
                <select 
                  value={mysteryKey} 
                  onChange={(e) => { handleMysteryChange(e.target.value); }} 
                    className="w-full bg-white/90 backdrop-blur border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    {Object.keys(MYSTERIES).map((key) => (
                        <option key={key} value={key}>{MYSTERIES[key].title}</option>
                    ))}
                </select>
             </div>
             
             <div className="absolute top-4 right-4 z-10 text-right">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Progresso</h3>
                <div className="text-xl font-bold text-blue-600">{progress}%</div>
             </div>

             <VisualRosary currentNodeId={currentStep.beadId} activeMysteryIndex={currentStep.mysteryIndex} />
             
             <div className="hidden lg:block absolute bottom-4 text-center w-full px-4">
                 <p className="text-xs text-slate-400">Teclado: Espaço ou Seta Direta</p>
             </div>
        </div>

        {/* Right Column: Prayer Content */}
        <div className="flex flex-col justify-center space-y-4 order-1 lg:order-2">
            
            {/* Header Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-bold text-blue-600 uppercase tracking-wide">{MYSTERIES[mysteryKey].title}</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight">
                    {currentStep.isMysteryStart || currentStep.mysteryIndex !== undefined ? 
                        `${(currentStep.mysteryIndex || 0) + 1}º Mistério` : 
                        'Orações Iniciais'
                    }
                </h1>
                {currentMysteryInfo && (
                    <div className="mt-3 pl-4 border-l-4 border-blue-400 bg-blue-50/50 py-2 rounded-r-lg">
                        <p className="text-lg text-slate-800 font-serif italic">
                            "{currentMysteryInfo}"
                        </p>
                    </div>
                )}
            </div>

            {/* Prayer Card */}
            <div className="flex-1 bg-white rounded-3xl shadow-xl p-6 md:p-8 border border-slate-100 flex flex-col items-center justify-between text-center relative min-h-[300px]">
                
                <div className="w-full">
                   <div className="mb-4 flex justify-center">
                        <span className="px-4 py-1.5 bg-blue-100/50 text-blue-800 text-sm font-bold rounded-full uppercase tracking-wide border border-blue-100">
                            {currentStep.label}
                        </span>
                   </div>

                   <div id="prayer-text" className="prose prose-lg px-2 overflow-y-auto max-h-[250px] md:max-h-[300px] w-full scrollbar-thin scrollbar-thumb-blue-200">
                        <p className="text-xl md:text-2xl text-slate-700 leading-relaxed font-serif">
                            {currentStep.prayer}
                        </p>
                   </div>
                </div>

                {/* Sub-label for intentions etc */}
                {currentStep.subLabel && (
                    <p className="text-sm text-slate-400 mt-4 mb-6 max-w-md mx-auto italic">
                        {currentStep.subLabel}
                    </p>
                )}

                {/* Controls */}
                <div className="flex items-center gap-3 w-full max-w-sm mt-auto pt-6 border-t border-slate-100">
                    <button 
                        onClick={prev} 
                        disabled={index === 0}
                        className="p-3 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-slate-600"
                        title="Anterior"
                    >
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    
                    <button 
                        onClick={next} 
                        disabled={index === steps.length - 1}
                        className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg shadow-blue-200 font-semibold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 disabled:shadow-none"
                    >
                        Próximo
                        <ChevronRight className="w-5 h-5" />
                    </button>

                    <button 
                        onClick={reset}
                        className="p-3 rounded-xl bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        title="Reiniciar"
                    >
                        <RotateCcw className="w-5 h-5" />
                    </button>
                </div>
            </div>

        </div>
      </div>
    </div>
  );
}
